import { Loader2, Minus, Plus, ShoppingBag, ShoppingBasket, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { printUrl } from "../lib/api";
import { useBasket } from "../context/BasketContext";
import { useAuth } from "../context/AuthContext";

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

// Header button: basket with a live count. Signed-in makers only (the basket lives on their account).
export const BasketButton = ({ className = "" }) => {
  const basket = useBasket();
  const { isAuthed } = useAuth();
  if (!basket || !isAuthed) return null;
  return (
    <Button data-testid="nav-basket-btn" variant="outline" className={`rounded-full gap-2 ${className}`} onClick={() => basket.setOpen(true)} aria-label="Basket">
      <ShoppingBasket className="h-4 w-4" />
      <span className="hidden sm:inline">Basket</span>
      {basket.count > 0 && (
        <span data-testid="nav-basket-count" className="grid place-items-center h-5 min-w-5 px-1 rounded-full bg-accent text-accent-foreground text-[11px] font-bold">
          {basket.count}
        </span>
      )}
    </Button>
  );
};

const Line = ({ item, sizes, busy, onUpdate, onRemove }) => (
  <div data-testid={`basket-line-${item.id}`} className={`rounded-2xl border p-3 flex gap-3 ${item.problem ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`}>
    <img src={printUrl(item.duck_id)} alt={item.name} className="h-16 w-16 rounded-xl object-cover border border-border shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold truncate">
          {item.name}
          <span className="ml-1.5 font-mono2 text-xs text-muted-foreground font-normal">Rev {item.version || 1}</span>
        </p>
        <button data-testid={`basket-remove-${item.id}`} onClick={() => onRemove(item.id)} disabled={busy} className="text-muted-foreground hover:text-destructive shrink-0" aria-label="Remove">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {item.problem && <p className="text-xs text-destructive font-semibold">{item.problem}</p>}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          data-testid={`basket-size-${item.id}`}
          value={item.height}
          disabled={busy}
          onChange={(e) => onUpdate(item.id, { height: Number(e.target.value) })}
          className="h-8 rounded-full border border-border bg-background px-3 text-xs font-semibold"
        >
          {sizes.map((s) => (
            <option key={s.height} value={s.height} disabled={!s.available}>
              {s.height} mm · {s.price}
              {s.available ? "" : " · sold out"}
            </option>
          ))}
        </select>
        <div className="flex items-center rounded-full border border-border h-8">
          <button data-testid={`basket-minus-${item.id}`} className="px-2 h-full disabled:opacity-40" disabled={busy || item.quantity <= 1} onClick={() => onUpdate(item.id, { quantity: item.quantity - 1 })} aria-label="One less">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span data-testid={`basket-qty-${item.id}`} className="w-6 text-center text-xs font-bold">{item.quantity}</span>
          <button data-testid={`basket-plus-${item.id}`} className="px-2 h-full disabled:opacity-40" disabled={busy || item.quantity >= 10} onClick={() => onUpdate(item.id, { quantity: item.quantity + 1 })} aria-label="One more">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {item.colors ? <span className="text-[11px] text-muted-foreground font-mono2">{item.colors} colours</span> : null}
      </div>
    </div>
  </div>
);

export const BasketDrawer = () => {
  const b = useBasket();
  if (!b) return null;
  const { basket, open, setOpen, busy, checkingOut, update, remove, clear, checkout } = b;
  const items = basket?.items || [];
  const ready = basket?.count || 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="basket-drawer" className="w-full sm:max-w-md overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl flex items-center gap-2">
            <ShoppingBasket className="h-5 w-5 text-accent" /> Your basket
          </SheetTitle>
          <SheetDescription>Ducks you want printed. They stay here on your account until you check out, on any device.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div data-testid="basket-empty" className="mt-16 text-center text-muted-foreground">
            <span className="text-5xl block mb-4">🧺</span>
            Your basket is empty. Add ducks from My Ducks or a duck's page.
          </div>
        ) : (
          <>
            <div className="mt-6 grid gap-3">
              {items.map((it) => (
                <Line key={it.id} item={it} sizes={basket.sizes || []} busy={busy} onUpdate={update} onRemove={remove} />
              ))}
            </div>

            <div className="mt-auto pt-6 space-y-3">
              {basket.sent_at && (
                <p className="text-xs text-muted-foreground">
                  Sent to your cart {fmtDate(basket.sent_at)}. Ducks stay here until the order comes through, so you can send them again anytime.
                </p>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">
                  {ready} {ready === 1 ? "duck" : "ducks"} · plus shipping
                </span>
                <span data-testid="basket-total" className="font-heading text-2xl font-bold">{basket.total || "—"}</span>
              </div>
              <Button
                data-testid="basket-checkout-btn"
                onClick={checkout}
                disabled={!ready || checkingOut || busy || !basket.enabled}
                className="w-full h-14 rounded-full text-base font-bold gap-2 duck-shadow"
              >
                {checkingOut ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingBag className="h-5 w-5" />}
                {basket.enabled ? `Go to cart · ${ready} ${ready === 1 ? "duck" : "ducks"}` : "Ordering isn't available right now"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Your ducks go into your cart at {basket.store}, each with its own picture, ready to check out. Each duck's link, size and filament list travel with the order.
              </p>
              <button data-testid="basket-clear-btn" onClick={clear} disabled={busy} className="w-full text-xs text-muted-foreground underline hover:text-foreground">
                Empty the basket
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
